import * as go from 'gojs';
import { noop } from 'vet-bones/utils';

import { ProjectResourceType } from 'src/constants/project';
import { GeometryString } from 'src/containers/ProjectDiagram/constants/geometries';
import { Layer } from 'src/containers/ProjectDiagram/constants/layers';
import {
  DELETED_OUTLINE_COLOR,
  FILE_UPLOAD_ICON_HEIGHT_RATIO,
  FILE_UPLOAD_ICON_NODE_RATIO,
  FILE_UPLOAD_ICON_TEXT_WIDTH_RATIO,
  FILE_UPLOAD_ICON_WIDTH_RATIO,
  FILE_UPLOAD_MASK_COLOR,
  NODE_FONT_COLOR,
  NODE_FONT_FACE,
  NODE_FONT_SIZE,
  NODE_FONT_WEIGHT,
  NODE_RESOURCE_SIZE,
  RESOURCE_COLOR,
  RESOURCE_COMBINED_ICON_RATIO,
  RESOURCE_ICON_MASK_STROKE_WIDTH,
  RESOURCE_ICON_RATIO,
  VIRTUAL_GRAPH_ICON_HEIGHT_RATIO,
  VIRTUAL_GRAPH_ICON_NODE_RATIO,
  VIRTUAL_GRAPH_ICON_WIDTH_RATIO,
  VIRTUAL_GRAPH_MASK_COLOR,
} from 'src/containers/ProjectDiagram/constants/styles';
import {
  graphObjectMouseEnter,
  graphObjectMouseLeave,
} from 'src/containers/ProjectDiagram/handlers/graphObject';
import { ExtendedNode } from 'src/containers/ProjectDiagram/templates/extensions/node';
import {
  dataBinding,
  modelBinding,
  objectBinding,
} from 'src/containers/ProjectDiagram/templates/helpers/binding';
import { isResourceModified } from 'src/containers/ProjectDiagram/templates/helpers/draft';
import {
  determineColorFromSelection,
  determineOpacityFromSelection,
} from 'src/containers/ProjectDiagram/templates/helpers/styles';
import {
  DeletedResource,
  DeletedVirtualGraph,
  Resource,
  VirtualGraph,
} from 'src/containers/ProjectDiagram/types/data';
import { ResourceNode } from 'src/containers/ProjectDiagram/types/diagram';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import {
  isDeletedVirtualGraph,
  isVirtualGraph,
} from 'src/containers/ProjectDiagram/utils/data';
import { getDataSourceIcon } from 'src/templates/icons';

const getVirtualGraphIcon = (resource: VirtualGraph | DeletedVirtualGraph) =>
  getDataSourceIcon({
    dataSourceType: resource.dataSourceType,
    enforceSquareDimensions: true,
  });

const isCustomVirtualGraphIcon = (
  resource: Resource | DeletedResource
): resource is VirtualGraph | DeletedVirtualGraph =>
  (isVirtualGraph(resource) || isDeletedVirtualGraph(resource)) &&
  !getVirtualGraphIcon(resource).isBlueprintIcon;

const $ = go.GraphObject.make;

const getMaskFillBinding = () =>
  dataBinding<go.Shape, Resource | DeletedResource>()('fill', '', (resource) =>
    resource.type === ProjectResourceType.FILE_UPLOAD
      ? FILE_UPLOAD_MASK_COLOR
      : VIRTUAL_GRAPH_MASK_COLOR
  );
const getMaskStrokeBinding = () =>
  dataBinding<go.Shape, Resource | DeletedResource>()(
    'stroke',
    '',
    (resource) =>
      resource.type === ProjectResourceType.FILE_UPLOAD
        ? FILE_UPLOAD_MASK_COLOR
        : VIRTUAL_GRAPH_MASK_COLOR
  );
const getGeometryStringBinding = (getMask: boolean) =>
  dataBinding<go.Shape, Resource | DeletedResource>()(
    'geometryString',
    '',
    (resource) => {
      let pathString;
      if (isVirtualGraph(resource) || isDeletedVirtualGraph(resource)) {
        pathString = GeometryString.ROUNDED_SQUARE;
        if (!isCustomVirtualGraphIcon(resource)) {
          pathString = getMask
            ? GeometryString.VIRTUAL_GRAPH_HALO
            : GeometryString.VIRTUAL_GRAPH;
        }
      } else {
        pathString = getMask
          ? GeometryString.FILE_UPLOAD_HALO
          : GeometryString.FILE_UPLOAD;
      }
      return go.Geometry.fillPath(pathString);
    }
  );
const getColorBinding = (deleted: boolean) => (prop: 'fill' | 'stroke') =>
  modelBinding<go.Shape, ModelData>()(
    prop,
    '',
    determineColorFromSelection(
      isResourceModified,
      deleted ? DELETED_OUTLINE_COLOR : RESOURCE_COLOR
    )
  );
const getStrokeWidthBinding = () =>
  dataBinding<go.Shape, Resource | DeletedResource>()(
    'strokeWidth',
    '',
    (resource) =>
      isCustomVirtualGraphIcon(resource) ? RESOURCE_ICON_MASK_STROKE_WIDTH : 0
  );
const getWidthBinding = () =>
  dataBinding<go.Shape, Resource | DeletedResource>()('width', '', (resource) =>
    isCustomVirtualGraphIcon(resource)
      ? NODE_RESOURCE_SIZE
      : NODE_RESOURCE_SIZE *
        (resource.type === ProjectResourceType.FILE_UPLOAD
          ? FILE_UPLOAD_ICON_NODE_RATIO * FILE_UPLOAD_ICON_WIDTH_RATIO
          : VIRTUAL_GRAPH_ICON_NODE_RATIO * VIRTUAL_GRAPH_ICON_WIDTH_RATIO)
  );
const getHeightBinding = () =>
  dataBinding<go.Shape, Resource | DeletedResource>()(
    'height',
    '',
    (resource) =>
      isCustomVirtualGraphIcon(resource)
        ? NODE_RESOURCE_SIZE
        : NODE_RESOURCE_SIZE *
          (resource.type === ProjectResourceType.FILE_UPLOAD
            ? FILE_UPLOAD_ICON_NODE_RATIO * FILE_UPLOAD_ICON_HEIGHT_RATIO
            : VIRTUAL_GRAPH_ICON_NODE_RATIO * VIRTUAL_GRAPH_ICON_HEIGHT_RATIO)
  );
const getIconSourceBinding = () =>
  dataBinding<go.Picture, Resource | DeletedResource>()(
    'source',
    '',
    (resource) => {
      let source = '';
      if (isCustomVirtualGraphIcon(resource)) {
        source = getVirtualGraphIcon(resource).icon;
      }
      return source;
    }
  );
const getIconOpacityBinding = () =>
  modelBinding<go.Shape, ModelData>()(
    'opacity',
    '',
    determineOpacityFromSelection()
  );
const getIconSizeBinding = (dimension: 'width' | 'height') =>
  dataBinding<go.Picture, Resource | DeletedResource>()(
    dimension,
    '',
    (resource) => {
      let size = NODE_RESOURCE_SIZE * RESOURCE_ICON_RATIO;
      if (
        isCustomVirtualGraphIcon(resource) &&
        getVirtualGraphIcon(resource).isCombinedIcon
      ) {
        size = NODE_RESOURCE_SIZE * RESOURCE_COMBINED_ICON_RATIO;
      }
      return size;
    }
  );
const getIconVisibilityBinding = () =>
  dataBinding<go.Picture, Resource | DeletedResource>()(
    'visible',
    '',
    (resource) => isCustomVirtualGraphIcon(resource)
  );

export const createResourceTemplate = ({
  deleted = false,

  contextMenu,
  handleClick = noop,
  handleDoubleClick = noop,
  toolTip,
}: {
  deleted?: boolean;

  contextMenu: go.HTMLInfo;
  handleClick?: NonNullable<ResourceNode['click']>;
  handleDoubleClick?: NonNullable<ResourceNode['doubleClick']>;
  toolTip: go.HTMLInfo;
}) =>
  $(
    ExtendedNode,
    go.Panel.Spot,
    {
      contextMenu,
      cursor: 'pointer',
      layerName: Layer.RESOURCE_BACKGROUND_LAYER,
      layoutConditions:
        // eslint-disable-next-line no-bitwise
        go.LayoutConditions.Standard & ~go.LayoutConditions.NodeSized,
      locationObjectName: 'MAIN',
      locationSpot: go.Spot.Center,
      selectionAdorned: false,
    },

    objectBinding<go.Node, go.Node>()('layerName', 'isSelected', (isSelected) =>
      isSelected
        ? Layer.RESOURCE_FOREGROUND_LAYER
        : Layer.RESOURCE_BACKGROUND_LAYER
    ),
    dataBinding<go.Node, Resource | DeletedResource>()(
      'position',
      'pos',
      (pos) => go.Point.parse(pos || '')
    ).makeTwoWay(go.Point.stringify),

    $(
      go.Panel,
      go.Panel.Spot,
      {
        click: handleClick,
        doubleClick: handleDoubleClick,
        mouseEnter: graphObjectMouseEnter,
        mouseLeave: graphObjectMouseLeave,
        toolTip,
      },
      $(
        go.Shape,
        {
          name: 'MASK',
          width: NODE_RESOURCE_SIZE,
          height: NODE_RESOURCE_SIZE,
          geometryString: '',
        },
        getMaskFillBinding(),
        getMaskStrokeBinding(),
        getGeometryStringBinding(true),
        getWidthBinding(),
        getHeightBinding()
      ),
      $(
        go.Shape,
        {
          name: 'MAIN',
          isPanelMain: true,
          width: NODE_RESOURCE_SIZE,
          height: NODE_RESOURCE_SIZE,
          fill: RESOURCE_COLOR,
          // Although the stroke is obscured by ICON_FRAME, MAIN still needs to
          // sometimes have a stroke to ensure than incoming arrowheads aren't
          // covered by ICON_FRAME's border
          stroke: RESOURCE_COLOR,
          strokeWidth: 0,
          geometryString: '',
          portId: '',
          fromLinkable: true,
          fromLinkableDuplicates: true,
        },
        getGeometryStringBinding(false),
        getColorBinding(deleted)('fill'),
        getColorBinding(deleted)('stroke'),
        getStrokeWidthBinding(),
        getWidthBinding(),
        getHeightBinding()
      ),
      $(
        go.Shape,
        {
          name: 'ICON_FRAME',
          width: NODE_RESOURCE_SIZE,
          height: NODE_RESOURCE_SIZE,
          stroke: RESOURCE_COLOR,
          strokeWidth: RESOURCE_ICON_MASK_STROKE_WIDTH,
          geometryString: GeometryString.ROUNDED_SQUARE,
          visible: true,
        },
        getMaskFillBinding(),
        getGeometryStringBinding(true),
        getIconVisibilityBinding(),
        getColorBinding(deleted)('stroke')
      ),
      $(
        go.Picture,
        {
          name: 'ICON',
          width: NODE_RESOURCE_SIZE * RESOURCE_ICON_RATIO,
          height: NODE_RESOURCE_SIZE * RESOURCE_ICON_RATIO,
          source: '',
          visible: true,
        },
        getIconSourceBinding(),
        getIconVisibilityBinding(),
        getIconOpacityBinding(),
        getIconSizeBinding('width'),
        getIconSizeBinding('height')
      ),
      $(
        go.Panel,
        'Vertical',
        {
          name: 'LABEL BACKGROUND',
          alignment: go.Spot.Bottom,
          alignmentFocus: new go.Spot(0.5, 0, 0, -4),
          // the transparent color ensures the label background be drawn, or there is a gap
          // between the label title and the label count where the diagram background is exposed
          background: 'transparent',
        },
        $(
          go.TextBlock,
          {
            name: 'LABEL TEXT',
            alignment: go.Spot.Bottom,
            stroke: NODE_FONT_COLOR,
            font: `${NODE_FONT_WEIGHT} ${NODE_FONT_SIZE}px ${NODE_FONT_FACE}`,
            overflow: go.TextOverflow.Ellipsis,
            wrap: go.Wrap.DesiredSize,
            width:
              FILE_UPLOAD_ICON_TEXT_WIDTH_RATIO *
              NODE_RESOURCE_SIZE *
              FILE_UPLOAD_ICON_WIDTH_RATIO,
            textAlign: 'center',
            maxLines: 1,
          },
          dataBinding<go.TextBlock, Resource | DeletedResource>()('text'),
          modelBinding<go.TextBlock, ModelData>()(
            'stroke',
            '',
            determineColorFromSelection(
              isResourceModified,
              deleted ? DELETED_OUTLINE_COLOR : NODE_FONT_COLOR
            )
          ),
          objectBinding<go.TextBlock, go.Node>()('scale', '', (node) =>
            node.isHighlighted || node.isSelected ? 1.2 : 1
          )
        )
      )
    )
  );
